/**
 * forecastChart — dependency-free SVG for the rate-search left-bar mini chart.
 *
 * ratesearch is a Node-16 CRA (react-scripts 3.1.2) with no charting library, so
 * we hand-build a compact inline SVG: solid history line, dashed forecast line,
 * and a shaded confidence band. Pure (data in -> SVG string out) so it's unit-
 * testable and previewable without React. The React wrapper injects the string.
 *
 * Customer-facing: pass the SELLING series (forecastSelling), never raw cost.
 */

const esc = (n) => Math.round(n * 100) / 100;

/** Normalize forecastSelling (object {points:[...]} OR a bare array) to an array. */
export function toForecastPoints(forecast) {
  if (!forecast) return [];
  const arr = Array.isArray(forecast) ? forecast : Array.isArray(forecast.points) ? forecast.points : [];
  return arr.filter((p) => p && Number.isFinite(Number(p.price)));
}

/** epoch ms (first of month, UTC) from "YYYY-MM" / "YYYY-MM-DD"; null if unparseable. */
export function monthTs(v) {
  const m = /^(\d{4})-(\d{2})/.exec(String(v || ""));
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, 1) : null;
}

/** epoch ms `n` whole months after `t`. */
function addMonths(t, n) {
  const d = new Date(t);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1);
}

/**
 * Clean, dated series for the expanded chart: history sorted oldest→newest with
 * future-dated anomalies dropped (history should be in the past — the forecast
 * covers the future), and the forecast placed one month per step after the last
 * history point. Pure.
 * @returns {{hist:{t:number,price:number}[], fc:{t:number,price:number,lo:number,hi:number}[]}}
 */
export function buildForecastSeries({ history = [], forecast = null, todayISO } = {}) {
  const now = todayISO ? Date.UTC(+todayISO.slice(0, 4), +todayISO.slice(5, 7), 1) : Date.now();
  const hist = (history || [])
    .map((h) => ({ t: monthTs(h.month || h.validityFrom), price: Number(h.price) }))
    .filter((p) => p.t && Number.isFinite(p.price) && p.t <= now)
    .sort((a, b) => a.t - b.t);
  const lastT = hist.length ? hist[hist.length - 1].t : now;
  const fc = toForecastPoints(forecast)
    .map((p) => ({ t: addMonths(lastT, p.step || 0), price: Number(p.price), lo: Number(p.lo), hi: Number(p.hi) }))
    .filter((p) => Number.isFinite(p.price));
  return { hist, fc };
}

/**
 * @param {{
 *   history?: {price:number}[],   // newest-first (as the API returns); reversed internally
 *   forecast?: {price:number, lo:number, hi:number}[],
 *   width?:number, height?:number, pad?:number,
 *   colorHist?:string, colorFc?:string, colorBand?:string
 * }} opts
 * @returns {string} SVG markup ("" if not enough data)
 */
export function renderForecastSvg(opts = {}) {
  const {
    width = 260, height = 92, pad = 8,
    colorHist = "#16788B", colorFc = "#B87514", colorBand = "rgba(184,117,20,0.15)",
  } = opts;

  const hist = [...(opts.history || [])].reverse().map((h) => Number(h.price)).filter(Number.isFinite);
  const fc = toForecastPoints(opts.forecast);
  if (hist.length < 2 && fc.length < 1) return "";

  // combined value range (history prices + forecast band) for a shared y-scale
  const vals = [...hist];
  for (const p of fc) { vals.push(Number(p.price), Number(p.lo), Number(p.hi)); }
  const finite = vals.filter(Number.isFinite);
  const min = Math.min(...finite), max = Math.max(...finite);
  const span = max - min || 1;

  const n = hist.length + fc.length;
  const innerW = width - pad * 2, innerH = height - pad * 2;
  const x = (i) => esc(pad + (n <= 1 ? 0 : (i / (n - 1)) * innerW));
  const y = (v) => esc(pad + innerH - ((Number(v) - min) / span) * innerH);

  const histPts = hist.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  // forecast continues from the last history point
  const fcStartIdx = hist.length - 1;
  const fcLine = fc.map((p, i) => `${x(fcStartIdx + 1 + i)},${y(p.price)}`);
  if (hist.length) fcLine.unshift(`${x(fcStartIdx)},${y(hist[hist.length - 1])}`);

  // band polygon (hi across, then lo back)
  const bandTop = fc.map((p, i) => `${x(fcStartIdx + 1 + i)},${y(p.hi)}`);
  const bandBot = fc.map((p, i) => `${x(fcStartIdx + 1 + i)},${y(p.lo)}`).reverse();
  const band = bandTop.concat(bandBot).join(" ");

  const dividerX = hist.length ? x(fcStartIdx) : pad;

  return (
    `<svg viewBox="0 0 ${width} ${height}" width="100%" preserveAspectRatio="none" role="img" aria-label="rate history and forecast">` +
    (band ? `<polygon points="${band}" fill="${colorBand}" />` : "") +
    `<line x1="${dividerX}" y1="${pad}" x2="${dividerX}" y2="${height - pad}" stroke="#D7DEE8" stroke-width="1" stroke-dasharray="2 2" />` +
    (histPts ? `<polyline points="${histPts}" fill="none" stroke="${colorHist}" stroke-width="1.6" />` : "") +
    (fcLine.length > 1 ? `<polyline points="${fcLine.join(" ")}" fill="none" stroke="${colorFc}" stroke-width="1.6" stroke-dasharray="4 3" />` : "") +
    `</svg>`
  );
}
